import { ref, computed, onBeforeMount, onMounted, onUnmounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useHead } from '@unhead/vue'

import { useNotifyStore } from '../stores/notify'
import { useSourcepointStore } from '../stores/sourcepoint'
import { useSessionStore } from '../stores/session'
import { useConfigStore } from '../stores/config'
import { usePageStore } from '../stores/page'
import { useAdaStore } from '../stores/ada'
import { runWhenIdle } from '../utils/scheduler'

import type { NotificationType } from '../stores/notify'
import type { Ref } from 'vue'
import type { ComposedBody, ComposedMetaData, ComposedPageMeta, ComposedParagraphTeaser } from '@xcel-next/composer-schema'
import type { Brand } from '@xcel-next/core/enums'
import type { Script, Style } from '@unhead/vue'

type StructureTag = {
  name?: string,
  url?: string
}

type SeedingData = {
  enabled?: boolean,
  headlineId?: string,
  articleId?: string
}

type PageContentMetaData = ComposedMetaData & {
  seeding?: SeedingData
}

type PageContentProps = {
  asset?: ComposedBody,
  metaData?: PageContentMetaData,
  pageMeta?: ComposedPageMeta,
  taboolaEnabled?: boolean,
  brand?: Brand
}

type SourcepointWindow = Window & {
  _sp_?: { loadPrivacyManagerModal?: () => void },
  openPrivacyManager?: () => void
}

type PageContentSetupDeps = {
  hasMobileAds: Ref<boolean>,
  isTablet: Ref<boolean>,
  viewportWidth: Ref<number | null>,
  deviceType: Ref<string | null>,
  screenOrientation: Ref<string | null>,
  getQueryParams: () => Record<string, string>,
  getAdSizePredictionStyleData: () => Style,
  initAdaSlotLoader: () => Promise<void>,
  initAdaDynamicSlotLoader: () => void
}

export function usePageContentShared (props: PageContentProps, deps: PageContentSetupDeps) {
  const notifyStore = useNotifyStore()
  const sourcepointStore = useSourcepointStore()
  const sessionStore = useSessionStore()
  const configStore = useConfigStore()
  const pageStore = usePageStore()
  const adaStore = useAdaStore()

  const { isPrintPreview, isArticleOrArticleVersion, pageData } = storeToRefs(pageStore)
  const { rsConfig } = storeToRefs(configStore)
  const { asideEnabled } = storeToRefs(adaStore)
  const { hasPurSub } = storeToRefs(sessionStore)

  const showMessage = ref(false)
  const assetTags = ref<StructureTag[]>([])

  const isRootUrl = computed(() => props.pageMeta?.url === '/')

  const showHeadline = computed(() => {
    return (props.asset?.headline?.length ?? 0) > 0 && (!isRootUrl.value || !rsConfig.value.isSeoHeadlineEnabled)
  })

  const showAuthors = computed(() => {
    return (isArticleOrArticleVersion.value && !props.metaData?.seeding) ||
      (props.pageMeta?.pageType === 'structureterm' && props.pageMeta?.structureTermType === 'structure' && !!props.asset?.author)
  })

  const combineAuthors = computed(() => {
    const authors: ComposedParagraphTeaser[] = []
    if (props.asset?.author) {
      authors.push(props.asset.author)
    }
    if (props.asset?.additionalAuthors?.length) {
      authors.push(...props.asset.additionalAuthors)
    }
    return authors
  })

  // For cases where the molten bundle should be loaded, but the ads shouldn't be initialised
  const shouldLoadMoltenBundle = computed(() => {
    return !hasPurSub.value && !process.env.AD_FREE_BRANDS?.includes(props.brand ?? '')
  })

  useHead(computed(() => {
    const metaInfo: { script: Script[], style: Style[] } = {
      script: [],
      style: []
    }
    metaInfo.style.push(deps.getAdSizePredictionStyleData())
    return metaInfo
  }))

  // gated by a hash of the expected src, so the cookie can't be abused to load arbitrary scripts
  function loadToolkitAutoLoadScript () {
    const hashCode = (s: string) => (s.split('').reduce((a, b) => (a = ((a << 5) - a) + b.charCodeAt(0)) & a, 0) >>> 0).toString(16)
    const toolkitAutoLoad = document.cookie.split('; ').find(row => row.startsWith('toolkitAutoLoad='))?.split('=')[1] || false
    const src = `/js/${toolkitAutoLoad}.js`
    if (hashCode(src) === '987cdce9' && !document.querySelectorAll(`script[src="${src}"]`).length) {
      const s = document.createElement('script')
      s.src = src
      s.async = true
      s.dataset.vmid = 'toolkitAutoLoad'
      document.head.appendChild(s)
    }
  }

  function moltenBundleScriptElement (position: 'top' | 'bottom'): HTMLScriptElement {
    const s = document.createElement('script')
    s.type = 'text/javascript'
    s.async = true
    s.src = adaStore.loadPreviewBundle
      ? `https://adctrl.emsmobile.de/molten/preview/${rsConfig.value.adSlotUsage?.moltenBundlePreviewBrandPath || props.brand}/MoltenBundle.${position}.js`
      : `${rsConfig.value.cdnUri}/moltenBundles/${props.brand}/MoltenBundle.${position}.js`
    s.dataset.vmid = `molten-bundle-${position}`
    return s
  }

  function appendMoltenBundleTopScript () {
    document.head.appendChild(moltenBundleScriptElement('top'))
  }

  function appendMoltenBundleBottomScript () {
    document.body.appendChild(moltenBundleScriptElement('bottom'))
    if (adaStore.getAdDefendScript) {
      const s = document.createElement('script')
      s.type = 'text/javascript'
      s.textContent = typeof adaStore.getAdDefendScript === 'string' ? adaStore.getAdDefendScript : '<!-- no script available in cache -->'
      s.dataset.vmid = 'ad-defend'
      document.body.appendChild(s)
    }
  }

  async function initAdaBasedOnPlacementLogic () {
    if (adaStore.dynamicAdSlotsEnabled) {
      deps.initAdaDynamicSlotLoader()
    } else {
      // must be awaited: initAdaSlotLoader yields between slot checks, so callers that need
      // MoltenBundle configured (window.bxMb/MoltenBundle.cmd) afterwards have to wait for it
      await deps.initAdaSlotLoader()
    }
  }

  function handleTabletViewport () {
    if (import.meta.env.SSR) return
    if (deps.deviceType.value === 'tablet' && deps.screenOrientation.value === 'portrait') {
      document.querySelector('[data-viewport="main"]')?.remove()
      const viewportMeta = document.createElement('meta')
      viewportMeta.setAttribute('data-vmid', 'bx-viewport')
      viewportMeta.name = 'viewport'
      const screenWidth = window.screen.width
      viewportMeta.content = `width=1165, user-scalable=yes, initial-scale=${screenWidth / 1165}`
      document.head.appendChild(viewportMeta)
      pageStore.setViewportMetaModified(true)
    }
  }

  function assignAssetTags () {
    let tags: StructureTag[] = []
    if (props.asset?.mainStructurePath) {
      tags = [...props.asset.mainStructurePath]
    }
    if (props.asset?.additionalStructures) {
      tags = [...tags, ...props.asset.additionalStructures]
    }
    assetTags.value = tags
      .filter((tag, index, self) => self.findIndex(e => e.name === tag.name) === index)
      .filter(tag => tag.url !== '/')
      .filter(tag => !(rsConfig.value.tagListExcludeTags ?? []).find((url: string) => url === tag.url))
  }

  async function openPrivacyManager () {
    // sometimes sourcepoint is not loaded or the window.openPrivacyManager is not defined on component mount, so we retry here
    // waiting for document.readyState === 'complete' is not enough, because the sourcepoint script is loaded async and theres no event when its loaded that we can listen to
    let retryCount = 0
    while (retryCount < 25) {
      // apparently waiting for window._sp_ is not enough, the function gets attached to the _sp_ object during runtime. big fuck
      const win = window as SourcepointWindow
      if (typeof win._sp_?.loadPrivacyManagerModal === 'function' && typeof win.openPrivacyManager === 'function') {
        win.openPrivacyManager()
        break
      } else {
        retryCount++
        // wait for 200ms before next attempt
        await new Promise(resolve => setTimeout(resolve, 200))
      }
    }
  }

  function checkShowNotification () {
    const showNotification = deps.getQueryParams()?.notify
    if (showNotification) {
      const params = new URLSearchParams(window.location.search)
      params.delete('notify')
      const cleanUrl = `${window.location.origin}${window.location.pathname}` + ([...params].length ? `?${params}` : '')
      window.history.pushState({ path: cleanUrl }, '', cleanUrl)
    }

    const messages: Record<string, { title: string, message: string, type?: NotificationType }> = {
      'activation-success': {
        type: 'info',
        title: 'Aktivierung abgeschlossen',
        message: `Herzlich willkommen bei ${rsConfig.value.actualBrandName}. Du bist jetzt direkt eingeloggt und kannst starten.`
      },
      'login-success': {
        title: 'Anmeldung erfolgreich',
        message: 'Du bist jetzt eingeloggt.'
      },
      'change-password-success': {
        title: 'Speichern erfolgreich',
        message: 'Deine neues Passwort wurde erfolgreich gespeichert.'
      },
      'logout-success': {
        title: 'Erfolgreich abgemeldet',
        message: 'Du hast dich erfolgreich abgemeldet.'
      },
      'account-deleted': {
        title: 'Löschen erfolgreich',
        message: 'Dein Konto wurde erfolgreich gelöscht.'
      }
    }

    if (showNotification && messages[showNotification]) {
      notifyStore.showNotification(messages[showNotification])
    }
  }

  function handleFirstTab (e: KeyboardEvent) {
    // watch out for tab key to identify keyboard users
    if (e.key === 'Tab') {
      document.body.classList.add('bx-js-user-is-tabbing')
      window.removeEventListener('keydown', handleFirstTab)
      window.addEventListener('mousedown', handleMouseDownOnce)
    }
  }

  function handleMouseDownOnce () {
    document.body.classList.remove('bx-js-user-is-tabbing')
    window.removeEventListener('mousedown', handleMouseDownOnce)
    window.addEventListener('keydown', handleFirstTab)
  }

  watch(() => sourcepointStore.consentsExist, async (newVal) => {
    if (shouldLoadMoltenBundle.value && newVal) {
      appendMoltenBundleTopScript()
      // wait for MoltenBundle to be configured before loading the bundle script itself
      await initAdaBasedOnPlacementLogic()
      appendMoltenBundleBottomScript()
    }
  })

  assignAssetTags()

  // tracks the idle-deferred initAdaBasedOnPlacementLogic() kicked off in onBeforeMount, so
  // onMounted can wait for MoltenBundle to be configured before loading the bundle script itself
  let adaInitStarted: Promise<void> | undefined

  onBeforeMount(() => {
    handleTabletViewport()
    if (shouldLoadMoltenBundle.value && sourcepointStore.consentsExist) {
      appendMoltenBundleTopScript()
      // Runs as soon as the browser has idle time, but no later than the timeout below,
      // so ad slot registration doesn't compete with the first paint/interaction while
      // still starting within a negligible, bounded delay.
      adaInitStarted = new Promise((resolve) => {
        runWhenIdle(() => {
          initAdaBasedOnPlacementLogic().then(resolve)
        })
      })
    }
  })

  onMounted(() => {
    if (shouldLoadMoltenBundle.value && sourcepointStore.consentsExist) {
      // don't block the rest of onMounted below on this - just make sure the bottom script
      // isn't appended until MoltenBundle has actually been configured
      void adaInitStarted?.then(() => {
        appendMoltenBundleBottomScript()
      })
    }
    // Show PrivacyManager with revoke button if not PUR and only if consents and URL Param exist
    const showPrivacyManagerModal = new URL(window.location.href).searchParams.get('showprivacymanager')
    if (showPrivacyManagerModal && !hasPurSub.value && sourcepointStore.consentsExist) {
      openPrivacyManager()
    }

    showMessage.value = !!(!import.meta.env.SSR && window.location.search.toLowerCase().includes('utm_campaign=doi-success'))
    checkShowNotification()
    window.addEventListener('keydown', handleFirstTab)
    loadToolkitAutoLoadScript()
  })

  onUnmounted(() => {
    window.removeEventListener('keydown', handleFirstTab)
    window.removeEventListener('mousedown', handleMouseDownOnce)
  })

  return {
    isRootUrl,
    showHeadline,
    showMessage,
    showAuthors,
    combineAuthors,
    assetTags,
    isPrintPreview,
    isArticleOrArticleVersion,
    pageData,
    rsConfig,
    asideEnabled
  }
}
